/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import IScene from "./scene_interface";
import {vec2, vec3} from "gl-matrix";
import {math} from "js_renderer";
import AbstractElement from "./elements/abstract_element";
import Point from "./elements/point";

export default
class PrimitivePoint {
    private _sceneAPI: IScene;
    private _element: AbstractElement;
    private _pointInfo: {
        position: vec3;
        geometryObjectUID: number;
        primitiveIndex: number;
        vertexIndex: number;
        equalsIn3D: (aPointInfo: any) => boolean;
        equals: (aPointInfo: any) => boolean;
    };

    constructor(aSceneAPI: IScene) {
        this._pointInfo = {
            position: vec3.create(),
            geometryObjectUID: -1,
            primitiveIndex: -1,
            vertexIndex: -1,
            equalsIn3D: function(aPointInfo: any) {
                return vec3.dist(this.position, aPointInfo.position) < math.FLOAT_PRECISION;
            },
            equals: function(aPointInfo) {
                return this.geometryObjectUID === aPointInfo.geometryObjectUID &&
                    this.primitiveIndex === aPointInfo.primitiveIndex &&
                    this.vertexIndex === aPointInfo.vertexIndex;
            }
        };

        this._element = null;
        this._sceneAPI = aSceneAPI;
    }

    public equals(aPrimitivePoint) {
        return this._pointInfo.equals(aPrimitivePoint._pointInfo);
    }

    public equalsIn3D(aPrimitivePoint) {
        return this._pointInfo.equalsIn3D(aPrimitivePoint._pointInfo);
    }

    public highlight(): boolean {
        if (this._element) {
            return false;
        }

        this._element = this._createPointAPIElement([16.0, 0.0]);
        this._element.draw(true);

        return true;
    }

    public removeHighlight(): boolean {
        if (!this._element) {
            return false;
        }

        this._element.remove();
        this._element = null;

        return true;
    }

    /**
     * Set picked primitive point info
     *
     * @param {object} aPrimitivePointInfo Picked primitive point
     */
    public setInfo(aPrimitivePointInfo: any) {
        this._pointInfo.position = vec3.clone(aPrimitivePointInfo.position);
        this._pointInfo.geometryObjectUID = aPrimitivePointInfo.geometryObjectUID;
        this._pointInfo.primitiveIndex = aPrimitivePointInfo.primitiveIndex;
        this._pointInfo.vertexIndex = aPrimitivePointInfo.vertexIndex;
    }

    /**
     * Return copy of the position in 3D scene.
     *
     * @returns {vec3} Positon in 3D
     */
    public getPosition(): vec3 {
        return vec3.clone(this._pointInfo.position);
    }

    /**
     * Return copy of the inverse x-rotate the point via x-rotation because during export to webgl, the model is x-rotated.
     *
     * @returns {vec3} Positon in 3D
     */
    public getCadworkPosition(): vec3 {
        return vec3.fromValues(this._pointInfo.position[0], -this._pointInfo.position[2], this._pointInfo.position[1]);
    }

    private _createPointAPIElement(aGlyphPosition: vec2): AbstractElement {
        let element: any = this._sceneAPI.getDocument().createAPIElement(Point.TypeName);
        this._sceneAPI.getDocument().attachElementToInternalModel(element);
        element.setPosition(this._pointInfo.position);
        // these are default properties so it is not necessary to set them here
        element.extendProperties({
            minPointSize: 12.0,
            maxPointSize: 12.0,
            glyphPosition: [aGlyphPosition[0] / Point.texture_size[0], aGlyphPosition[1] / Point.texture_size[1]],
            alwaysOnTop: true,
            pickable: false
        });

        return element;
    }
}
